/**
 * Brave Search Adapter
 * Implementation for Brave Search API (privacy-focused)
 * https://brave.com/search/api/
 */

import type { IWebSearchAdapter, WebSearchOptions } from './IWebSearchAdapter';
import type {
  BraveSearchConfig,
  WebSearchResponse,
  WebSearchResult,
  RateLimitInfo,
} from '@/types/webSearch.types';
import { getBackendUrl, getAuthHeaders, prepareWebSearchQuery } from './webSearchUtils';

const BRAVE_DEFAULT_ENDPOINT = 'https://api.search.brave.com/res/v1/web/search';

interface BraveWebResult {
  title?: string;
  url?: string;
  description?: string;
  published_date?: string;
}

interface BraveApiResponse {
  web?: {
    results?: BraveWebResult[];
  };
}

export class BraveAdapter implements IWebSearchAdapter {
  public readonly provider = 'brave';
  private config: BraveSearchConfig | null = null;

  /**
   * Initialize the Brave adapter with configuration
   */
  async initialize(config: BraveSearchConfig): Promise<void> {
    if (!config.subscriptionToken || config.subscriptionToken.trim() === '') {
      throw new Error('Brave subscription token is required');
    }

    if (config.provider !== 'brave') {
      throw new Error(`Invalid provider: expected 'brave', got '${config.provider}'`);
    }

    this.config = config;
  }

  /**
   * Execute a web search using Brave Search API via backend proxy
   */
  async search(query: string, options?: WebSearchOptions): Promise<WebSearchResponse> {
    if (!this.config) {
      throw new Error('Adapter not initialized. Call initialize() first.');
    }

    if (!query || query.trim() === '') {
      throw new Error('Search query cannot be empty');
    }

    const startTime = Date.now();

    try {
      // Call backend proxy to avoid CORS issues
      const response = await fetch(`${getBackendUrl()}/web-search`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          provider: 'brave',
          query: prepareWebSearchQuery(query, 'brave'),
          originalQueryLength: query.trim().length,
          config: {
            subscriptionToken: this.config.subscriptionToken,
            endpoint: this.config.endpoint || BRAVE_DEFAULT_ENDPOINT,
            maxResults: this.config.maxResults || 10,
            country: this.config.country,
            freshness: this.config.freshness,
          },
        }),
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Brave API error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const data = await response.json() as BraveApiResponse;

      // Normalize Brave response to our standard format
      const webResults = data.web?.results || [];
      const results: WebSearchResult[] = webResults.map((result: BraveWebResult) => ({
        title: result.title || '',
        url: result.url || '',
        snippet: result.description || '',
        publishedDate: result.published_date ? new Date(result.published_date) : undefined,
        relevanceScore: undefined, // Brave doesn't provide relevance scores
      }));

      return {
        query,
        results,
        totalResults: results.length,
        metadata: {
          provider: 'brave',
          took: Date.now() - startTime,
          cached: false,
        },
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Brave search failed: ${error.message}`);
      }
      throw new Error('Brave search failed with unknown error');
    }
  }

  /**
   * Test connection by performing a simple search
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.search('test connection');
      return true;
    } catch (error) {
      console.error('Brave connection test failed:', error);
      return false;
    }
  }

  /**
   * Brave doesn't provide rate limit information in response
   */
  async getRateLimitInfo(): Promise<RateLimitInfo | null> {
    return null;
  }
}
